/**
 * PATH: packages/email-hub/src/feature/components/EmailThread.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENTS-EMAILTHREAD-001
 * PURPOSE: Orchestrates the Gmail-style stacked thread reader used by BOTH
 *          reading surfaces (the full-page `EmailReader` and the inbox
 *          split-view `EmailPreviewPane`). Fetches the conversation via
 *          `useEmailThread`, renders one colour-accented `ThreadMessage`
 *          block per message (with labelled dividers between them), wires a
 *          single shared `AttachmentPreviewModal` over a FLAT thread-wide
 *          attachment list, and pins a `ThreadAttachmentBar` to the bottom
 *          of the scroll container that colour-codes every attachment by its
 *          source message.
 * WHY:     Operator request — stacked thread with clear per-message dividers
 *          + a sticky, colour-coded attachment bar so files tie back to the
 *          message they arrived on. Falls back to a single neutral block
 *          (today's behaviour) for un-threaded mail or a failed thread fetch.
 * FLOW:
 *   openedEmail → useEmailThread → [messages] → per-message colour map →
 *   ThreadMessage blocks (+ dividers) + flat attachment list → bottom bar +
 *   one shared preview modal.
 * RELATED:
 *   - components/ThreadMessage.tsx · ThreadAttachmentBar.tsx
 *   - lib/threadColors.ts · lib/quoteTrim.ts
 *   - hooks/useEmails.ts (useEmailThread)
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Ref } from 'react'
import { FileText, Forward, Paperclip, Reply, ReplyAll } from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import { seedFromIntent, useComposeDockStore } from '../compose/composeDockStore'
import { useEmailThread } from '../hooks/useEmails'
import type { Email, EmailAttachment } from '../hooks/useEmails'
import { useFindInThread } from '../hooks/useFindInThread'
import {
  isStructuredDocCategory,
  useEmailStatementViewer,
} from './StatementViewer'
import { selectVisibleAttachments } from '../lib/utils'
import {
  colorForIndex,
  NEUTRAL_THREAD_COLOR,
  type ThreadColor,
} from '../lib/threadColors'
import ThreadMessage from './ThreadMessage'
import ThreadAttachmentBar, { type ThreadAttachmentGroup } from './ThreadAttachmentBar'
import AttachmentPreviewModal from './AttachmentPreviewModal'
import {
  distanceFromScrollEnd,
  findOverflowYParent,
  snapAnchorToEnd,
  THREAD_SNAP_SLACK_PX,
} from './snapThreadToLatest'

export interface EmailThreadProps {
  /** The email the operator opened — the freshest copy (signed inline URLs,
   *  full body). Always rendered, and used as the conversation anchor. */
  openedEmail: Email
  /** Bubble up so the host can refetch the opened email after side-effects. */
  onRefetch?: () => void
  /** Body zoom (full reader passes its zoom; preview pane omits → 1). */
  zoom?: number
  /** Forwarded to the wrapper around all message bodies so the host's
   *  inline-image interaction / recovery hooks (which delegate from a single
   *  container root) keep working across the stacked bodies. */
  bodyContainerRef?: Ref<HTMLDivElement>
  className?: string
  /** True while the opened email's detail GET (full body_html) is in
   *  flight — forwarded to ITS ThreadMessage so the body shows a
   *  skeleton instead of flashing the one-line preview. */
  openedBodyLoading?: boolean
  /** Active find-in-thread query (G5). While non-blank, messages whose
   *  body contains a match are FORCE-expanded (and their quoted-history
   *  section force-shown when the hit lives there) so the host's DOM
   *  highlighter can reach text that collapsed blocks would otherwise
   *  keep out of the DOM. Blank/undefined → normal expand behaviour. */
  findQuery?: string
  /** When true, only the latest message starts open. Older messages stay
   *  collapsed headers until the operator opens them. The Emails hub leaves
   *  this off (every message starts open). Kanban turns it on so a chain
   *  in the rail or a popup is a stack, not one long page. */
  collapseOlder?: boolean
}

export default function EmailThread({
  openedEmail,
  onRefetch: _onRefetch,
  zoom,
  bodyContainerRef,
  className,
  openedBodyLoading,
  findQuery,
  collapseOlder = false,
}: EmailThreadProps) {
  const { thread, fetchThread, loading: threadLoading } = useEmailThread(
    openedEmail.conversation_id ? openedEmail.id : null,
  )

  useEffect(() => {
    if (openedEmail.conversation_id) {
      void fetchThread()
    }
  }, [fetchThread, openedEmail.conversation_id])

  // Resolve the rendered message list. Prefer the fetched thread (chrono
  // order, each item carries its own body_html + attachments); swap in the
  // freshest `openedEmail` for its own row so its signed inline URLs win.
  // Fall back to a single-block render before the thread lands / on failure.
  const messages = useMemo<Email[]>(() => {
    if (!thread || thread.length === 0) return [openedEmail]
    const swapped = thread.map((m) => (m.id === openedEmail.id ? openedEmail : m))
    if (!swapped.some((m) => m.id === openedEmail.id)) {
      return [...swapped, openedEmail]
    }
    return swapped
  }, [thread, openedEmail])

  const isSingle = messages.length <= 1
  const colorFor = useCallback(
    (i: number): ThreadColor => (isSingle ? NEUTRAL_THREAD_COLOR : colorForIndex(i)),
    [isSingle],
  )

  // Operator 2026-06-13 ("by default have all sections expanded — you
  // don't want to click when scrolling down"). Every message in the
  // stacked thread starts EXPANDED so the conversation reads top-to-
  // bottom by scrolling, with no per-block clicks. Quoted history is
  // still trimmed per message (splitNewVsQuoted) behind each block's own
  // "Show quoted text" toggle, so expand-all shows each message's NEW
  // content — not the duplicated chain N times. The header toggle still
  // collapses any block on demand (manual overrides survive refetch).
  const defaultExpanded = useMemo(() => {
    const set = new Set<string>()
    if (collapseOlder) {
      const latest = messages[messages.length - 1]
      if (latest) set.add(latest.id)
      return set
    }
    for (const m of messages) set.add(m.id)
    return set
  }, [messages, collapseOlder])

  const [overrides, setOverrides] = useState<Record<string, boolean>>({})

  // Find-in-thread (G5): per-message match counts computed from the RAW
  // bodies (new + quoted halves), independent of what is rendered. While
  // a query is live, any message with a hit is force-expanded — a
  // collapsed block has no text nodes, so the host's DOM highlighter
  // could never paint (or count) its matches. Manual collapse overrides
  // are preserved and re-apply the moment the find closes.
  const findState = useFindInThread(messages, findQuery)
  const isExpanded = (id: string): boolean => {
    if (findState[id]) return true
    return id in overrides ? overrides[id] : defaultExpanded.has(id)
  }
  const toggleExpand = (id: string) =>
    setOverrides((prev) => ({ ...prev, [id]: !(id in prev ? prev[id] : defaultExpanded.has(id)) }))

  // Stable per-message scroll-to refs.
  const refs = useRef(new Map<string, HTMLDivElement | null>())
  const refSetters = useRef(new Map<string, (el: HTMLDivElement | null) => void>())
  const getSetRef = (id: string) => {
    let fn = refSetters.current.get(id)
    if (!fn) {
      fn = (el: HTMLDivElement | null) => {
        if (el) refs.current.set(id, el)
        else refs.current.delete(id)
      }
      refSetters.current.set(id, fn)
    }
    return fn
  }

  // Flat thread-wide attachment list (one shared modal) + per-message groups
  // for the bottom bar. Both derive from the SAME selectVisibleAttachments
  // projection so the modal index and the bar/per-message click stay aligned.
  const { groups, flatAttachments, flatStartByEmail } = useMemo(() => {
    const groups: ThreadAttachmentGroup[] = []
    const flat: EmailAttachment[] = []
    const startByEmail: Record<string, number> = {}
    messages.forEach((m, i) => {
      const vis = selectVisibleAttachments(m.attachments)
      startByEmail[m.id] = flat.length
      groups.push({
        email: m,
        color: colorFor(i),
        attachments: vis,
        flatStartIndex: flat.length,
      })
      flat.push(...vis)
    })
    return { groups, flatAttachments: flat, flatStartByEmail: startByEmail }
  }, [messages, colorFor])

  const [previewIndex, setPreviewIndex] = useState<number | null>(null)
  const openFromMessage = (emailId: string, localIndex: number) => {
    const start = flatStartByEmail[emailId] ?? 0
    setPreviewIndex(start + localIndex)
  }

  // Reply / Reply all / Forward open the shared compose dock seeded from a
  // specific message. Per-message rows pass their own id; the bottom bar
  // targets the latest message in the chain (the one you usually reply to).
  const openCompose = useCallback(
    (emailId: string, intent: 'reply' | 'reply_all' | 'forward') => {
      useComposeDockStore
        .getState()
        .openOrRestore(seedFromIntent(intent, String(emailId)))
    },
    [],
  )
  const latestMessage = messages[messages.length - 1]

  // Opening a thread used to leave the pane at the oldest message, so the
  // operator had to scroll the whole chain to read the newest one. Snap to
  // the bottom when the selection or the fetched chain changes, and stop
  // once they scroll back up to older mail.
  const stickToLatest = useRef(true)
  const snappingToLatest = useRef(false)
  useEffect(() => {
    stickToLatest.current = true
  }, [openedEmail.id])

  useEffect(() => {
    const anchor = latestMessage ? refs.current.get(latestMessage.id) ?? null : null
    const scroller = findOverflowYParent(anchor)
    if (!scroller) return
    const onScroll = () => {
      if (snappingToLatest.current) return
      if (distanceFromScrollEnd(scroller) > THREAD_SNAP_SLACK_PX) {
        stickToLatest.current = false
      }
    }
    scroller.addEventListener('scroll', onScroll, { passive: true })
    return () => scroller.removeEventListener('scroll', onScroll)
  }, [openedEmail.id, latestMessage?.id, messages.length])

  useEffect(() => {
    if (!latestMessage || !stickToLatest.current) return
    const snap = () => {
      if (!stickToLatest.current) return
      const anchor = refs.current.get(latestMessage.id)
      if (!anchor) return
      // The scroll event from this assignment is synchronous. Hold the flag
      // only across that assignment so a later operator scroll still counts.
      snappingToLatest.current = true
      snapAnchorToEnd(anchor)
      snappingToLatest.current = false
    }
    snap()
    const frame = requestAnimationFrame(snap)
    return () => cancelAnimationFrame(frame)
  }, [openedEmail.id, latestMessage?.id, messages.length, openedBodyLoading])

  /**
   * The bottom bar and the per-message rows are the same three actions on the
   * same store; the ONLY difference is which message id gets seeded. The bar
   * always targets the latest message, and that message renders its own row
   * whenever it is expanded (ThreadMessage keeps the row inside its expanded
   * body) — which is the default for every message. So on the email the
   * operator was looking at, a single-message one, the bar was three buttons
   * that did exactly what the three buttons above them did (2026-08-13).
   *
   * It still earns its place when the latest message is COLLAPSED: there is no
   * per-message row to duplicate then, and replying to the newest message
   * without expanding it is what a Gmail-style foot bar is for.
   */
  const latestOwnsItsReplyRow = latestMessage
    ? isExpanded(latestMessage.id)
    : false

  // Optional host-injected structured statement/bill viewer. Shown only
  // for finance-category mail (e.g. an auto-scraped Xero statement) and
  // only when a host wired the handler — view-only, never allocation.
  const { openStructuredStatement } = useEmailStatementViewer()
  const showStatementViewer =
    !!openStructuredStatement && isStructuredDocCategory(openedEmail.category)
  const structureCtaLabel = ['bill', 'invoice', 'payment'].includes(
    (openedEmail.category || '').toLowerCase(),
  )
    ? 'View bill structure'
    : 'View statement (structured)'

  // Scroll-spy: highlight the group whose message is most in view.
  const [activeEmailId, setActiveEmailId] = useState<string | null>(null)
  const onJump = useCallback((emailId: string) => {
    setOverrides((prev) => ({ ...prev, [emailId]: true }))
    setActiveEmailId(emailId)
    // Defer so a just-expanded block has laid out before we scroll to it.
    requestAnimationFrame(() => {
      refs.current.get(emailId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }, [])

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return
    const els = messages
      .map((m) => refs.current.get(m.id))
      .filter((el): el is HTMLDivElement => !!el)
    if (els.length === 0) return
    const ratios = new Map<string, number>()
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const id = (e.target as HTMLElement).dataset.messageId || ''
          if (!id) continue
          if (e.isIntersecting) ratios.set(id, e.intersectionRatio)
          else ratios.delete(id)
        }
        let best: string | null = null
        let bestRatio = -1
        for (const m of messages) {
          const r = ratios.get(m.id)
          if (r !== undefined && r > bestRatio) {
            bestRatio = r
            best = m.id
          }
        }
        if (best) setActiveEmailId(best)
      },
      { threshold: [0.1, 0.5, 0.9] },
    )
    els.forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [messages])

  return (
    <div className={cn('relative', className)} data-testid="email-thread">
      <div ref={bodyContainerRef} className="space-y-0">
        {messages.map((m, i) => (
          <Fragment key={m.id}>
            {i > 0 && !isSingle && (
              <ThreadDivider color={colorFor(i)} label={dividerLabel(m)} />
            )}
            <ThreadMessage
              email={m}
              color={colorFor(i)}
              expanded={isExpanded(m.id)}
              onToggleExpand={() => toggleExpand(m.id)}
              zoom={zoom}
              onPreview={openFromMessage}
              setRef={getSetRef(m.id)}
              isActive={activeEmailId === m.id}
              onReply={(intent) => openCompose(m.id, intent)}
              bodyLoading={m.id === openedEmail.id ? openedBodyLoading : undefined}
              findMatches={findState[m.id]}
              forceShowQuoted={(findState[m.id]?.inQuoted ?? 0) > 0}
            />
          </Fragment>
        ))}
      </div>

      {/* Structured statement/bill viewer — opens the host's read-only
          invoice/statement reader (BillDataPanel) for an auto-scraped Xero
          statement, instead of leaving the email. Hidden unless a host
          wired the handler. */}
      {showStatementViewer && (
        <div
          data-testid="thread-statement-viewer"
          className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-3"
        >
          <button
            type="button"
            onClick={() => openStructuredStatement?.(String(openedEmail.id))}
            className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-primary/5 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10"
            title="Open line items and totals in the structured bill/statement reader (view-only)"
            data-testid="thread-bill-structure"
          >
            <FileText className="h-4 w-4" /> {structureCtaLabel}
          </button>
        </div>
      )}

      {/* Bottom reply bar — the Gmail-style affordance at the foot of the
          conversation, targeting the latest message in the chain. Suppressed
          while that message shows its own reply row, because then the two are
          the same actions on the same message (see latestOwnsItsReplyRow). */}
      {latestMessage && !latestOwnsItsReplyRow && (
        <div
          data-testid="thread-reply-bar"
          className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-3"
        >
          <span className="mr-1 text-xs text-muted-foreground">
            Reply to {dividerLabel(latestMessage)}:
          </span>
          <button
            type="button"
            onClick={() => openCompose(latestMessage.id, 'reply')}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent"
          >
            <Reply className="h-4 w-4" /> Reply
          </button>
          <button
            type="button"
            onClick={() => openCompose(latestMessage.id, 'reply_all')}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent"
            title="Reply to sender + all original recipients"
          >
            <ReplyAll className="h-4 w-4" /> Reply all
          </button>
          <button
            type="button"
            onClick={() => openCompose(latestMessage.id, 'forward')}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-sm font-medium text-foreground hover:bg-accent"
          >
            <Forward className="h-4 w-4" /> Forward
          </button>
        </div>
      )}

      <ThreadAttachmentBar
        groups={groups}
        onPreview={(flatIndex) => setPreviewIndex(flatIndex)}
        onJump={onJump}
        activeEmailId={activeEmailId}
      />

      {/* Honest fallback for the "paperclip but nothing downloadable" case:
          a message can flag has_attachments while its only attachments are
          INLINE images (signature logos, QR codes, embedded screenshots),
          which render in the body and are correctly dropped from the chip
          list. Without this note the reader showed a SILENT BLANK ("where's
          the attachment?"). Only shown when the WHOLE conversation has zero
          downloadable files, so it never spams ordinary signature mail.

          Guarded against the hydration window: the BE lazy-fills attachment
          metadata on first GET, so a freshly-opened message can read
          has_attachments=true with an EMPTY attachments[] for a beat. Showing
          the note then is a false "no files" flash while "Loading
          attachments…" is still on screen - so suppress it while the thread
          is loading or any message's attachments are still hydrating. */}
      {!threadLoading &&
        !messages.some((m) => m.has_attachments && (m.attachments?.length ?? 0) === 0) &&
        flatAttachments.length === 0 &&
        messages.some((m) => m.has_attachments) && (
        <div
          data-testid="thread-inline-only-note"
          className="mt-3 flex items-center gap-2 px-1 text-[11px] text-muted-foreground"
        >
          <Paperclip className="h-3.5 w-3.5 shrink-0" />
          <span>
            No downloadable files in this conversation - the paperclip marks
            inline images (e.g. signature logos) embedded in the message.
          </span>
        </div>
      )}

      <AttachmentPreviewModal
        attachments={flatAttachments}
        initialIndex={previewIndex ?? 0}
        isOpen={previewIndex !== null}
        onClose={() => setPreviewIndex(null)}
        emailId={openedEmail.id}
      />
    </div>
  )
}

function dividerLabel(email: Email): string {
  return (
    email.from_name ||
    (email.from_address ?? '').split('@')[0] ||
    'Message'
  )
}

function ThreadDivider({ color, label }: { color: ThreadColor; label: string }) {
  return (
    <div className="flex items-center gap-2 my-3" data-testid="thread-divider" aria-hidden>
      <span className={cn('h-2 w-2 rounded-full shrink-0', color.dot)} />
      <span className="h-px flex-1 bg-border" />
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground truncate max-w-[50%]">
        {label}
      </span>
      <span className="h-px flex-1 bg-border" />
    </div>
  )
}
